/**
 * HelpHome — Help Center home page
 * Clean layout inspired by OpenAI help center (dark theme)
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { route } from 'preact-router';
import { setMetaTags, clearMetaTags, setCanonical, clearCanonical, setJsonLd, clearJsonLd, buildCanonicalUrl, buildBreadcrumbLd } from '../lib/seo';

import { T } from '../styles/tokens';
import { Button } from '../components/ui';
const SUPPORTED_LOCALES = [
  'pt-BR', 'en', 'es', 'fr', 'de', 'it', 'ja', 'ko', 'zh-CN', 'zh-TW', 'ru',
  'ar', 'hi', 'nl', 'pl', 'tr', 'sv', 'da', 'nb', 'fi',
];

function HreflangTags({ path }: { path: string }) {
  useEffect(() => {
    document.querySelectorAll('link[data-hreflang]').forEach((el) => el.remove());
    const base = '/help';
    const locales = SUPPORTED_LOCALES;
    for (const loc of locales) {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = loc;
      link.href = `${base}/${loc}${path}`;
      link.setAttribute('data-hreflang', 'true');
      document.head.appendChild(link);
    }
    const xd = document.createElement('link');
    xd.rel = 'alternate';
    xd.hreflang = 'x-default';
    xd.href = `${base}/en${path}`;
    xd.setAttribute('data-hreflang', 'true');
    document.head.appendChild(xd);
    return () => {
      document.querySelectorAll('link[data-hreflang]').forEach((el) => el.remove());
    };
  }, [path]);
  return null;
}

function getBasePrefix(): string {
  return typeof window !== 'undefined' && window.location.hostname.startsWith('help.') ? '' : '/help';
}

interface Category {
  id: string;
  slug: string;
  icon: string;
  name: string;
  description: string;
  entry_count: number;
}

const ICON_MAP: Record<string, string> = {
  megaphone: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
  layers: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  code: 'M16 18l6-6-6-6M8 6l-6 6 6 6',
  user: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
};

function CategoryIcon({ icon }: { icon: string }) {
  const path = ICON_MAP[icon] || ICON_MAP.layers;
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d={path} />
    </svg>
  );
}

const LOCALE_NAMES: Record<string, string> = {
  'pt-BR': 'Português (Brasil)',
  'en': 'English',
  'es': 'Español',
  'fr': 'Français',
  'de': 'Deutsch',
  'it': 'Italiano',
  'ja': '日本語',
  'ko': '한국어',
  'zh-CN': '中文(简体)',
  'zh-TW': '中文(繁體)',
  'ru': 'Русский',
  'ar': 'العربية',
  'hi': 'हिन्दी',
  'nl': 'Nederlands',
  'pl': 'Polski',
  'tr': 'Türkçe',
  'sv': 'Svenska',
  'da': 'Dansk',
  'nb': 'Norsk',
  'fi': 'Suomi',
};

export function HelpHomePage({ locale: propLocale }: { path?: string; locale?: string }) {
  const locale = propLocale || 'pt-BR';
  const basePrefix = getBasePrefix();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [locales, setLocales] = useState<any[]>([]);
  const [localeOpen, setLocaleOpen] = useState(false);

  useEffect(() => {
    api.get(`/cms/categories?contentType=help&locale=${locale}`).then((res: any) => {
      setCategories(res?.categories || []);
      setLoading(false);
    }).catch(() => setLoading(false));
    // Locales are hardcoded; CMS doesn't have a separate locales endpoint
    setLocales(SUPPORTED_LOCALES);
  }, [locale]);

  // SEO
  useEffect(() => {
    const url = buildCanonicalUrl(locale);
    setMetaTags({
      title: 'Help Center - WhiteLabelClaw',
      description: 'Find answers, guides, and documentation for WhiteLabelClaw. Browse categories or search our knowledge base.',
      url,
    });
    setCanonical(url);
    setJsonLd([
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'WhiteLabelClaw Help Center',
        url: '/help',
        potentialAction: {
          '@type': 'SearchAction',
          target: `/help/${locale}?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
      buildBreadcrumbLd([
        { name: 'Help Center', url: `/help/${locale}` },
      ]),
    ]);
    return () => { clearMetaTags(); clearCanonical(); clearJsonLd(); };
  }, [locale]);

  useEffect(() => {
    if (search.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(() => {
      setSearching(true);
      api.get(`/cms/search?contentType=help&q=${encodeURIComponent(search)}&locale=${locale}`).then((res: any) => {
        setSearchResults(res?.entries || res?.results || []);
        setSearching(false);
      }).catch(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [search, locale]);

  const changeLocale = (loc: string) => {
    setLocaleOpen(false);
    route(`${basePrefix}/${loc}`);
  };

  const currentLocaleName = LOCALE_NAMES[locale] || locale;

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: "'Inter', -apple-system, sans-serif", color: T.text }}>
      <HreflangTags path="" />

      {/* ── Header ── */}
      <header style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0 32px', height: 56,
        borderBottom: `1px solid ${T.bgHover}`,
      }}>
        {/* Left: brand */}
        <a href={`${basePrefix}/${locale}`} style={{ color: T.text, textDecoration: 'none', fontSize: 17, fontWeight: 700, letterSpacing: '-0.3px' }}>
          WhiteLabelClaw
        </a>

        {/* Right: locale + login */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Locale dropdown */}
          <div style={{ position: 'relative' as const }}>
            <Button variant="ghost"
              onClick={() => setLocaleOpen(!localeOpen)}
              style={{
                background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6,
                padding: '6px 12px', color: T.textSecondary, fontSize: 13, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z"/></svg>
              {currentLocaleName}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
            </Button>
            {localeOpen && (
              <div style={{
                position: 'absolute' as const, right: 0, top: '100%', marginTop: 4,
                background: T.bgSecondary, border: `1px solid ${T.bgActive}`, borderRadius: 8,
                minWidth: 180, maxHeight: 300, overflow: 'auto', zIndex: 100,
                boxShadow: `0 8px 32px ${T.blackAlpha50}`,
              }}>
                {(locales.length > 0 ? locales : SUPPORTED_LOCALES.map(l => ({ locale: l, native_name: LOCALE_NAMES[l] || l }))).map((l: any) => (
                  <Button variant="ghost"
                    key={l.locale}
                    onClick={() => changeLocale(l.locale)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left' as const, padding: '10px 14px',
                      background: l.locale === locale ? T.primaryBg : 'transparent',
                      border: 'none', color: l.locale === locale ? T.primary : T.textSecondary,
                      fontSize: 13, cursor: 'pointer',
                    }}
                    onMouseEnter={(e: any) => { if (l.locale !== locale) e.currentTarget.style.background = T.bgCardHover; }}
                    onMouseLeave={(e: any) => { if (l.locale !== locale) e.currentTarget.style.background = 'transparent'; }}
                  >
                    {l.native_name || LOCALE_NAMES[l.locale] || l.locale}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Login button */}
          <a
            href="/chat"
            style={{
              padding: '6px 16px', borderRadius: 6, fontSize: 13, fontWeight: 500,
              color: T.text, textDecoration: 'none',
              border: `1px solid ${T.whiteAlpha15}`, background: T.bgCard,
            }}
          >
            Login
          </a>
        </div>
      </header>

      {/* ── Search bar (full-width, clean) ── */}
      <div style={{ borderBottom: `1px solid ${T.border}`, padding: '20px 32px', position: 'relative' as const }}>
        <div style={{ maxWidth: 960, margin: '0 auto', position: 'relative' as const }}>
          <svg style={{ position: 'absolute' as const, left: 14, top: '50%', transform: 'translateY(-50%)', opacity: 0.4 }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            type="text"
            value={search}
            onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
            placeholder={locale === 'en' ? 'Search articles...' : locale === 'es' ? 'Buscar artículos...' : 'Pesquisar artigos...'}
            style={{
              width: '100%', padding: '12px 16px 12px 44px', fontSize: 15,
              border: `1px solid ${T.bgActive}`, borderRadius: 8,
              background: T.bgCard, color: T.text, outline: 'none',
              boxSizing: 'border-box' as const,
            }}
          />

          {/* Search results dropdown */}
          {search.length >= 2 && (
            <div style={{
              position: 'absolute' as const, left: 0, right: 0, top: '100%', marginTop: 4,
              background: T.bgSecondary, border: `1px solid ${T.bgActive}`, borderRadius: 10,
              maxHeight: 320, overflow: 'auto', zIndex: 50,
              boxShadow: `0 8px 32px ${T.blackAlpha50}`,
            }}>
              {searching ? (
                <div style={{ padding: 16, color: T.textSecondary, fontSize: 13 }}>Buscando...</div>
              ) : searchResults.length === 0 ? (
                <div style={{ padding: 16, color: T.textSecondary, fontSize: 13 }}>{locale === 'en' ? 'No results found' : 'Nenhum resultado encontrado'}</div>
              ) : searchResults.map((r: any) => (
                <a key={r.id} href={`${basePrefix}/${locale}/${r.category_slug}/${r.slug}`} style={{
                  display: 'block', padding: '12px 16px', color: T.text, textDecoration: 'none',
                  borderBottom: `1px solid ${T.border}`, transition: 'background 0.15s',
                }}
                  onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCardHover}
                  onMouseLeave={(e: any) => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{r.title}</div>
                  <div style={{ fontSize: 12, color: T.textMuted }}>{r.category_name} · {r.snippet || r.excerpt}</div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Categories grid ── */}
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '40px 32px 60px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 16,
        }}>
          {loading ? Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ background: T.bgCard, borderRadius: 10, padding: 24, height: 100 }} />
          )) : categories.map((cat) => (
            <a key={cat.id} href={`${basePrefix}/${locale}/${cat.slug}`} style={{
              display: 'flex', alignItems: 'flex-start', gap: 16, padding: 20, borderRadius: 10,
              textDecoration: 'none', background: T.bgCard,
              border: `1px solid ${T.border}`, transition: 'background 0.15s',
            }}
              onMouseEnter={(e: any) => e.currentTarget.style.background = T.border}
              onMouseLeave={(e: any) => e.currentTarget.style.background = T.bgCard}>
              {/* Icon circle */}
              <div style={{
                width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                background: T.primaryBg, color: T.primary,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CategoryIcon icon={cat.icon} />
              </div>
              {/* Text */}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 4 }}>{cat.name}</div>
                <div style={{ fontSize: 13, color: T.textMuted, lineHeight: '1.5' }}>{cat.description}</div>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Close locale dropdown on outside click */}
      {localeOpen && (
        <div
          onClick={() => setLocaleOpen(false)}
          style={{ position: 'fixed' as const, inset: 0, zIndex: 99 }}
        />
      )}
    </div>
  );
}
